import Konva from 'konva'
import QRCode from 'qrcode'
import type Examination from '@/models/customer/Examination'
import {formatAge, formatPostgresDate} from '@/utils/date'

export const ID_CARD_STYLES = ['classic', 'modern'] as const
export type IDCardStyle = typeof ID_CARD_STYLES[number]

export interface IDCardOptions {
  container: HTMLDivElement
  exam: Examination | null
  style?: string
  qrUrl?: string | null
  addReadingToDistance?: boolean
}

const W = 840.76
const H = 529.37
const M = 28
const INK = '#000000'
const FONT = 'Tajawal-Medium'

function num(value: number | string | undefined | null, withoutMinus = false, withoutPrecision = false): string {
  if (value == undefined || value === '') return ''
  if (withoutMinus && withoutPrecision) return `${value}`
  const v = Number(value)
  return v > 0 ? `+${v.toFixed(2)}` : `${v.toFixed(2)}`
}

function cardData(ex: Examination | null, addToDistance: boolean) {
  const dob = ex?.customer?.dateOfBirth
  let readingODSPH = ex?.readingODSPH
  let readingOSSPH = ex?.readingOSSPH
  if (ex?.readingAdd && addToDistance) {
    if (ex.readingODSPH != undefined) readingODSPH = ex.distanceODSPH! + ex.readingODSPH!
    if (ex.readingOSSPH != undefined) readingOSSPH = ex.distanceOSSPH! + ex.readingOSSPH!
  }
  return {
    name: (ex?.customer?.name || '').trim(),
    age: dob && String(dob) != '0001-01-01T00:00:00Z' ? formatAge(String(dob)) : '',
    examNo: `${ex?.externalID ?? ''}`,
    examDate: formatPostgresDate(String(ex?.createdAt), 'DD-MM-YYYY'),
    nextAppt: ex?.nextAppointment ? formatPostgresDate(String(ex.nextAppointment), 'DD-MM-YYYY') : '',
    optometrist: `${ex?.examinator?.name ?? ''}`,
    va: {
      headers: ['VA WOG', 'VA WG', 'IOP'],
      od: [ex?.vaWithoutGlassesOD || '', ex?.vaWithGlassesOD || '', ex?.iopODStatus || num(ex?.iopOD, true, true)],
      os: [ex?.vaWithoutGlassesOS || '', ex?.vaWithGlassesOS || '', ex?.iopOSStatus || num(ex?.iopOS, true, true)]
    },
    distance: {
      headers: ['SPH', 'CYL', 'AXIS', 'IPD'],
      od: [num(ex?.distanceODSPH), num(ex?.distanceODCYL), num(ex?.distanceODAXIS, true, true), num(ex?.distanceODIPD, true, true)],
      os: [num(ex?.distanceOSSPH), num(ex?.distanceOSCYL), num(ex?.distanceOSAXIS, true, true)]
    },
    near: {
      headers: ['SPH', 'CYL', 'AXIS', 'IPD'],
      od: [num(readingODSPH), num(ex?.readingODCYL), num(ex?.readingODAXIS, true, true), num(ex?.readingODIPD, true, true)],
      os: [num(readingOSSPH), num(ex?.readingOSCYL), num(ex?.readingOSAXIS, true, true)]
    },
    addTag: ex?.readingAdd && !addToDistance ? 'ADD' : undefined
  }
}

type CardData = ReturnType<typeof cardData>
type Drawer = (layer: Konva.Layer, d: CardData, hasQR: boolean) => {x: number, y: number, size: number}

function tools(layer: Konva.Layer) {
  const text = (value: string, x: number, y: number, size: number, opts: Record<string, any> = {}) => {
    const node = new Konva.Text({x, y, text: value, fontSize: size, fontFamily: FONT, fontStyle: 'bold', fill: INK, ...opts})
    layer.add(node)
    return node
  }
  const label = (value: string, x: number, y: number, opts: Record<string, any> = {}) =>
    text(value.toUpperCase(), x, y, 13, {fontStyle: 'normal', letterSpacing: 2, ...opts})
  const line = (x1: number, y1: number, x2: number, y2: number, width = 1) =>
    layer.add(new Konva.Line({points: [x1, y1, x2, y2], stroke: INK, strokeWidth: width, lineCap: 'round'}))
  return {text, label, line}
}

const drawClassic: Drawer = (layer, d) => {
  const {text, label, line} = tools(layer)
  label('Customer', M, 26)
  const NAME_W = 390
  let nameSize = 32
  const nameNode = text(d.name, M, 44, nameSize, {wrap: 'none'})
  while (nameNode.width() > NAME_W && nameSize > 12) {
    nameSize -= 1
    nameNode.fontSize(nameSize)
  }
  nameNode.y(44 + (32 - nameSize) * 0.55)
  const meta: [string, string, number][] = [['Age', d.age, 430], ['Exam No.', d.examNo, 548], ['Exam Date', d.examDate, 680]]
  for (const [title, value, x] of meta) {
    label(title, x, 26)
    text(value, x, 50, 22)
  }
  line(M, 98, W - M, 98, 2.5)
  line(M, 103, W - M, 103, 0.8)

  const HR = 26
  const TITLE_END = 198
  const EYE_END = 250
  const COLS = [EYE_END, 392, 534, 676, W - M]
  const section = (top: number, SH: number, title: string, s: CardData['va'],
                   opts: {mergeLast?: boolean, tag?: string, divider?: boolean} = {}) => {
    if (opts.divider) line(M, top + SH + 4, W - M, top + SH + 4, 0.6)
    const titleNode = text(title, M + 16, 0, 19, {width: TITLE_END - M - 24})
    const tagH = opts.tag ? 26 : 0
    titleNode.y(top + (SH - titleNode.height() - tagH) / 2)
    if (opts.tag) {
      const tagY = titleNode.y() + titleNode.height() + 6
      const tagNode = text(opts.tag, M + 26, tagY + 4, 14, {letterSpacing: 1.5})
      layer.add(new Konva.Rect({x: M + 16, y: tagY, width: tagNode.width() + 20, height: 22, cornerRadius: 11, stroke: INK, strokeWidth: 1}))
    }
    const rowsTop = top + HR + 8
    const row1 = rowsTop + (top + SH - rowsTop) / 4
    const row2 = rowsTop + (top + SH - rowsTop) * 3 / 4
    const eye = {x: TITLE_END, width: EYE_END - TITLE_END, align: 'center', fontStyle: 'normal'}
    text('OD', 0, row1 - 9, 18, eye)
    text('OS', 0, row2 - 9, 18, eye)
    s.headers.forEach((header, i) => {
      const cell = {x: COLS[i], width: COLS[i + 1] - COLS[i], align: 'center'}
      text(header, 0, top + 4, 15, {...cell, fontStyle: 'normal', letterSpacing: 1})
      if (opts.mergeLast && i === s.headers.length - 1) {
        text(s.od[i] || '', 0, (row1 + row2) / 2 - 11, 22, cell)
      } else {
        text(s.od[i] || '', 0, row1 - 11, 22, cell)
        text(s.os[i] || '', 0, row2 - 11, 22, cell)
      }
    })
  }
  const VA_TOP = 112
  const VA_H = 128
  section(VA_TOP, VA_H, '', d.va, {divider: true})
  section(250, 100, 'Distance (DV)', d.distance, {mergeLast: true, divider: true})
  section(360, 100, 'Near (NV)', d.near, {mergeLast: true, tag: d.addTag})

  line(M, 470, W - M, 470, 0.8)
  const optLabel = label('Optometrist', M, 490)
  text(d.optometrist, M + optLabel.width() + 14, 484, 22)
  if (d.nextAppt) {
    const appt = text(d.nextAppt, 0, 484, 22, {width: W - M, align: 'right'})
    const apptLabel = label('Next Appt.', 0, 490)
    apptLabel.x(W - M - appt.getTextWidth() - 14 - apptLabel.width())
  }
  const size = 122
  return {x: (M + TITLE_END) / 2 - size / 2 - 6, y: VA_TOP + (VA_H - size) / 2, size}
}

const drawModern: Drawer = (layer, d, hasQR) => {
  const {text, label, line} = tools(layer)
  const LW = 232
  const QR = 150
  const small = {fontSize: 11, letterSpacing: 1.6}
  const fitText = (value: string, x: number, y: number, size: number, width: number, maxLines: number, min = 12) => {
    const node = text(value, x, y, size, {width, wrap: 'word', lineHeight: 1.1})
    while (node.height() > size * 1.1 * maxLines + 1 && node.fontSize() > min) node.fontSize(node.fontSize() - 1)
    return node
  }
  const field = (title: string, value: string, x: number, y: number, width: number, size = 20) => {
    label(title, x, y, small)
    return fitText(value, x, y + 16, size, width, 1)
  }

  // left column: QR + customer details
  let y = hasQR ? M + QR + 18 : M
  label('Customer', M, y, small)
  const nameNode = fitText(d.name, M, y + 16, 24, LW - M - 4, 2, 13)
  if (/[\u0600-\u06FF]/.test(d.name)) nameNode.align('right')
  y += 16 + Math.max(nameNode.height(), 26) + 16
  const half = (LW - M) / 2
  field('Age', d.age, M, y, half - 6)
  field('Exam No.', d.examNo, M + half, y, half - 4)
  y += 56
  field('Exam Date', d.examDate, M, y, half - 6, 17)
  if (d.nextAppt) field('Next Appt.', d.nextAppt, M + half, y, half - 4, 17)
  label('Optometrist', M, H - M - 52, small)
  fitText(d.optometrist, M, H - M - 36, 18, LW - M - 4, 2, 11)
  line(LW + 8, M, LW + 8, H - M, 1.2)

  // right column: one table per section with its header in a solid black bar
  const X0 = LW + 30
  const X1 = W - M
  const TITLE_W = 172
  const colW = (X1 - X0 - TITLE_W) / 4
  const BAR = 38
  const ROW = 52
  const HEAD = 17
  const section = (top: number, title: string, s: CardData['va'], opts: {mergeLast?: boolean, tag?: string} = {}) => {
    layer.add(new Konva.Rect({x: X0, y: top, width: X1 - X0, height: BAR, fill: INK, cornerRadius: 6}))
    const headY = top + (BAR - HEAD) / 2 + 1
    const titleNode = text(title.toUpperCase(), X0 + 14, headY, HEAD, {fill: 'white', letterSpacing: 1})
    if (opts.tag) {
      const tx = X0 + 14 + titleNode.width() + 10
      layer.add(new Konva.Rect({x: tx, y: top + 7, width: 48, height: BAR - 14, cornerRadius: (BAR - 14) / 2, stroke: 'white', strokeWidth: 1.5}))
      text(opts.tag, tx, top + (BAR - 14) / 2 + 1, 14, {fill: 'white', width: 48, align: 'center', letterSpacing: 1})
    }
    s.headers.forEach((header, i) => {
      text(header, X0 + TITLE_W + colW * i, headY, HEAD, {fill: 'white', width: colW, align: 'center', letterSpacing: 1})
    })
    const r1 = top + BAR + ROW / 2
    const r2 = r1 + ROW
    text('OD', X0 + 14, r1 - 10, 19, {fontStyle: 'normal'})
    text('OS', X0 + 14, r2 - 10, 19, {fontStyle: 'normal'})
    s.headers.forEach((_, i) => {
      const cell = {x: X0 + TITLE_W + colW * i, width: colW, align: 'center'}
      if (opts.mergeLast && i === s.headers.length - 1) {
        text(s.od[i] || '', 0, (r1 + r2) / 2 - 13, 26, cell)
      } else {
        text(s.od[i] || '', 0, r1 - 13, 26, cell)
        text(s.os[i] || '', 0, r2 - 13, 26, cell)
      }
    })
  }
  const STEP = BAR + ROW * 2 + 22
  section(M, '', d.va)
  section(M + STEP, 'Distance (DV)', d.distance, {mergeLast: true})
  section(M + STEP * 2, 'Near (NV)', d.near, {mergeLast: true, tag: d.addTag})
  return {x: M, y: M, size: QR}
}

/** Draws the examination ID card; resolves once fonts and the QR code are on the stage. */
export async function renderIDCard(opts: IDCardOptions): Promise<Konva.Stage> {
  const stage = new Konva.Stage({container: opts.container, width: W, height: H})
  const layer = new Konva.Layer()
  stage.add(layer)
  layer.add(new Konva.Rect({x: 0, y: 0, width: W, height: H, fill: 'white'}))
  await document.fonts?.ready
  const draw = opts.style === 'modern' ? drawModern : drawClassic
  const qr = draw(layer, cardData(opts.exam, !!opts.addReadingToDistance), !!opts.qrUrl)
  if (opts.qrUrl) {
    const dataUrl = await QRCode.toDataURL(opts.qrUrl, {width: Math.round(qr.size * 3), margin: 0})
    const img = new Image()
    await new Promise(resolve => {
      img.onload = resolve
      img.onerror = resolve
      img.src = dataUrl
    })
    layer.add(new Konva.Image({x: qr.x, y: qr.y, image: img, width: qr.size, height: qr.size}))
  }
  layer.draw()
  return stage
}
